import { useEffect, useMemo, useState } from "react";
import { useRoute, Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, XCircle, MessageSquare, AlertTriangle, Loader2 } from "lucide-react";

type Action = "approved" | "rejected" | "request_changes";

interface Context {
  approverEmail: string;
  stage: string;
  expiresAt: string;
  approval: { id: number; contractId: number; currentStage: string; status: string };
  contract: {
    id: number;
    contractType: string;
    startDate: string;
    endDate: string;
    annualSalary: string;
    status: string;
  };
  coach: { firstName: string; lastName: string; sport: string; title: string };
  org: { institutionName: string };
  isActionable: boolean;
  staleReason: string | null;
}

const ACTION_META: Record<Action, { label: string; verb: string; color: string; icon: React.ElementType }> = {
  approved: { label: "Approve", verb: "approving", color: "bg-emerald-600 hover:bg-emerald-700", icon: CheckCircle2 },
  request_changes: { label: "Request Changes", verb: "requesting changes on", color: "bg-amber-600 hover:bg-amber-700", icon: MessageSquare },
  rejected: { label: "Reject", verb: "rejecting", color: "bg-red-600 hover:bg-red-700", icon: XCircle },
};

function formatStage(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMoney(s: string): string {
  const n = Number(s);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : s;
}

export default function ApprovalActionPage() {
  const [, params] = useRoute<{ token: string }>("/approval-action/:token");
  const token = params?.token ?? "";
  const search = typeof window !== "undefined" ? window.location.search : "";
  const initialAction = useMemo<Action>(() => {
    const q = new URLSearchParams(search).get("action");
    if (q === "approved" || q === "rejected" || q === "request_changes") return q;
    return "approved";
  }, [search]);

  const [ctx, setCtx] = useState<Context | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [action, setAction] = useState<Action>(initialAction);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ decision: Action } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/approvals/email-action/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (cancelled) return;
          setLoadError(
            body?.reason === "expired"
              ? "This action link has expired. Sign in to take action from the approval inbox."
              : body?.reason === "used"
                ? "This action link has already been used."
                : "This action link is no longer valid.",
          );
          return;
        }
        const data = (await res.json()) as Context;
        if (!cancelled) setCtx(data);
      } catch {
        if (!cancelled) setLoadError("Network error loading the approval. Please try again.");
      }
    }
    if (token) void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const requiresComment = action === "rejected" || action === "request_changes";
  const canSubmit = !!ctx?.isActionable && (!requiresComment || comment.trim().length > 0) && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`/api/approvals/email-action/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, comment: comment.trim() || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(typeof body?.error === "string" ? body.error : "Could not submit your decision.");
        return;
      }
      setDone({ decision: action });
    } catch {
      setSubmitError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full" />
          <span className="font-serif font-semibold tracking-tight text-slate-900">Zindeal</span>
          <span className="text-xs text-slate-500 ml-1">Approval action</span>
        </div>

        {loadError ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" /> Link unavailable
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">{loadError}</p>
              <Button asChild variant="outline">
                <Link href="/approvals">Open approval inbox</Link>
              </Button>
            </CardContent>
          </Card>
        ) : !ctx ? (
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64 mt-2" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ) : done ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" /> Decision recorded
              </CardTitle>
              <CardDescription>
                Thanks — your <strong>{ACTION_META[done.decision].label}</strong> on stage{" "}
                <strong>{formatStage(ctx.stage)}</strong> for {ctx.coach.firstName} {ctx.coach.lastName} has been
                logged.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link href={`/contracts/${ctx.contract.id}?tab=approval`}>Open in Zindeal</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle>Approval action</CardTitle>
                  <CardDescription>
                    {ctx.org.institutionName} · {formatStage(ctx.stage)} stage
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs">
                  Acting as {ctx.approverEmail}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md border bg-white p-4 text-sm">
                <div className="font-semibold text-slate-900">
                  {ctx.coach.firstName} {ctx.coach.lastName}
                </div>
                <div className="text-slate-600">
                  {ctx.coach.title} · {ctx.coach.sport}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                  <div>
                    <span className="text-slate-400">Type:</span> {ctx.contract.contractType.replace(/_/g, " ")}
                  </div>
                  <div>
                    <span className="text-slate-400">Salary:</span> {formatMoney(ctx.contract.annualSalary)}/yr
                  </div>
                  <div>
                    <span className="text-slate-400">Start:</span> {ctx.contract.startDate}
                  </div>
                  <div>
                    <span className="text-slate-400">End:</span> {ctx.contract.endDate}
                  </div>
                </div>
              </div>

              {!ctx.isActionable && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>This link can no longer be used</AlertTitle>
                  <AlertDescription>
                    {ctx.staleReason === "stage_advanced"
                      ? "The approval has moved past your stage."
                      : ctx.staleReason === "no_longer_approver"
                        ? "You're no longer listed as an approver for this stage."
                        : "This approval is no longer in progress."}
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Your decision
                </label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(Object.keys(ACTION_META) as Action[]).map((a) => {
                    const meta = ACTION_META[a];
                    const isActive = action === a;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => setAction(a)}
                        disabled={!ctx.isActionable}
                        className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                          isActive
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        data-testid={`action-${a}`}
                      >
                        <Icon className="h-4 w-4" /> {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Comment {requiresComment ? <span className="text-red-600">(required)</span> : "(optional)"}
                </label>
                <Textarea
                  className="mt-2"
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    requiresComment
                      ? "Briefly explain what should change…"
                      : "Optionally add a note for the drafter and other approvers"
                  }
                  disabled={!ctx.isActionable}
                />
              </div>

              {submitError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={submit}
                disabled={!canSubmit}
                className={`w-full text-white ${ACTION_META[action].color}`}
                data-testid="submit-decision"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>Confirm {ACTION_META[action].label}</>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                This single-use link expires {new Date(ctx.expiresAt).toLocaleString()}.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
